import './Navbar.scss';

import { Skeleton } from 'antd';
import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router';

import {
  ADMIN_ROUTES,
  FAQ_ROUTES,
  getFullAdminRoute
} from '@/components/Shared/routes/routes';
import { pathExpandLogo } from '@/constants';
import { useWindowSize } from '@/hooks/useWindowSize';
import AllurFinanceLogo from '@/media/allur-finance.svg?react';
import { SettingOutlined } from '@/media/antd-icons';
import ChevronDownIcon from '@/media/icons/chevron-down.svg?react';
import { useAppDispatch, useAppSelector } from '@/store';
import { setSidebarExpanded } from '@/store/userInfo.slice';
import { type TMenuItem } from '@/types/app';

type TMenuItemProps =
  | {
      isLoading: true;
      loadingOrder: number;
      menuItem?: never;
      onLinkClick?: never;
      level?: never;
    }
  | {
      isLoading?: false;
      loadingOrder?: number;
      menuItem: TMenuItem;
      onLinkClick: () => void;
      level?: number;
    };

const MenuItem: React.FC<TMenuItemProps> = ({
  isLoading,
  loadingOrder,
  menuItem,
  onLinkClick,
  level = 0
}) => {
  const location = useLocation();

  const hasChildren =
    !isLoading && !!menuItem.children && menuItem.children.length > 0;
  const isActive =
    !isLoading && !!menuItem.link && location.pathname.includes(menuItem.link);

  const hasActiveChild = (item: TMenuItem): boolean => {
    if (isLoading || !item.children) return false;
    for (const child of item.children) {
      const childLink =
        child.link || `${FAQ_ROUTES.root}${FAQ_ROUTES.faqArticle(child.id)}`;
      if (location.pathname.includes(childLink)) return true;
      if (hasActiveChild(child)) return true;
    }
    return false;
  };

  const shouldBeExpanded = !isLoading && hasActiveChild(menuItem);
  const [isExpanded, setIsExpanded] = useState(shouldBeExpanded);

  useEffect(() => {
    if (shouldBeExpanded) {
      setIsExpanded(true);
    }
  }, [location.pathname, shouldBeExpanded]);

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasChildren) {
      setIsExpanded(!isExpanded);
    }
  };

  if (isLoading)
    return (
      <li className="faq-navbar-menu-item">
        <div className="faq-navbar-menu-item__container">
          <Skeleton.Button
            active
            style={{
              width: `${180 - (loadingOrder % 5) * 15}px`,
              marginLeft: `${loadingOrder % 4 === 0 ? 0 : (loadingOrder % 3) * 20}px`
            }}
            size="small"
          />
        </div>
      </li>
    );
  return (
    <li className="faq-navbar-menu-item">
      <div
        className={`faq-navbar-menu-item__container ${menuItem.link || menuItem.externalLink ? 'faq-navbar-menu-item__container--link' : ''} ${hasChildren ? 'faq-navbar-menu-item__container--has-children' : ''} ${level === 0 ? 'faq-navbar-menu-item__container--root-level' : ''}`}
        onClick={
          !menuItem.link && !menuItem.externalLink && hasChildren
            ? handleToggle
            : undefined
        }
      >
        {menuItem.externalLink ? (
          <a
            href={menuItem.externalLink}
            target="_blank"
            onClick={onLinkClick}
            className="faq-navbar-menu-item__link"
          >
            {menuItem.label}
            {!!menuItem.extension && (
              <span className="faq-navbar-menu-item__extension">
                {menuItem.extension}
              </span>
            )}
          </a>
        ) : menuItem.link ? (
          <NavLink
            to={menuItem.link}
            onClick={onLinkClick}
            className={`faq-navbar-menu-item__link ${
              isActive ? 'faq-navbar-menu-item__link--active' : ''
            }`}
          >
            {menuItem.label}
          </NavLink>
        ) : (
          <span className="faq-navbar-menu-item__title">{menuItem.label}</span>
        )}
        {hasChildren && (
          <button
            type="button"
            onClick={handleToggle}
            className="faq-navbar-menu-item__chevron-button"
          >
            <ChevronDownIcon
              className={`faq-navbar-menu-item__chevron ${
                isExpanded ? 'faq-navbar-menu-item__chevron--expanded' : ''
              }`}
            />
          </button>
        )}
      </div>
      {hasChildren && isExpanded && (
        <ul className="faq-navbar-menu-item__sub-list">
          {menuItem.children?.map((child) => (
            <MenuItem
              key={child.id}
              menuItem={child}
              onLinkClick={onLinkClick}
              level={level + 1}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

const WIDTH_FOR_OPENED_FAQ_NAVBAR = 1000;

const Navbar: React.FC = () => {
  const dispatch = useAppDispatch();

  const {
    faqMenu,
    isSidebarExpanded,
    userAccesses: {
      global: { isAdmin }
    }
  } = useAppSelector((state) => state.userInfo);
  const { isLoadingFaqList } = useAppSelector((state) => state.faq);

  const [windowWidth] = useWindowSize();

  const onClick = () => {
    if (windowWidth < WIDTH_FOR_OPENED_FAQ_NAVBAR)
      dispatch(setSidebarExpanded(false));
  };

  useEffect(() => {
    if (windowWidth !== 0)
      dispatch(setSidebarExpanded(windowWidth > WIDTH_FOR_OPENED_FAQ_NAVBAR));
  }, [windowWidth]);

  if (isLoadingFaqList)
    return (
      <>
        <div
          onClick={() => dispatch(setSidebarExpanded(false))}
          className={`faq-navbar-overlay ${isSidebarExpanded ? 'faq-navbar-overlay--shown' : ''}`}
        />
        <aside
          className={`faq-navbar ${
            isSidebarExpanded ? 'faq-navbar--expanded' : 'faq-navbar--collapsed'
          }`}
        >
          <div className="faq-navbar__logo-box">
            <NavLink
              className="faq-navbar__logo"
              to={FAQ_ROUTES.root}
              onClick={onClick}
            >
              <AllurFinanceLogo className="faq-navbar__logo" />
            </NavLink>
            {windowWidth <= WIDTH_FOR_OPENED_FAQ_NAVBAR && (
              <button
                className="faq-navbar__arrow"
                onClick={() => dispatch(setSidebarExpanded(!isSidebarExpanded))}
              >
                <img src={pathExpandLogo} alt="logo_expand" />
              </button>
            )}
          </div>
          <nav className="faq-navbar__menu">
            <ul className="faq-navbar__menu-list">
              {Array.from({ length: 10 }).map((_, index) => (
                <MenuItem key={index} isLoading loadingOrder={index} />
              ))}
            </ul>
          </nav>
        </aside>
      </>
    );
  return (
    <>
      <div
        onClick={() => dispatch(setSidebarExpanded(false))}
        className={`faq-navbar-overlay ${isSidebarExpanded ? 'faq-navbar-overlay--shown' : ''}`}
      />
      <aside
        className={`faq-navbar ${
          isSidebarExpanded ? 'faq-navbar--expanded' : 'faq-navbar--collapsed'
        }`}
      >
        <div className="faq-navbar__logo-box">
          <NavLink
            className="faq-navbar__logo"
            to={FAQ_ROUTES.root}
            onClick={onClick}
          >
            <AllurFinanceLogo className="faq-navbar__logo" />
          </NavLink>
          {windowWidth <= WIDTH_FOR_OPENED_FAQ_NAVBAR && (
            <button
              className="faq-navbar__arrow"
              onClick={() => dispatch(setSidebarExpanded(!isSidebarExpanded))}
            >
              <img src={pathExpandLogo} alt="logo_expand" />
            </button>
          )}
        </div>

        <nav className="faq-navbar__menu">
          <ul className="faq-navbar__menu-list">
            {faqMenu.map((item) => (
              <MenuItem
                key={item.id}
                menuItem={item}
                onLinkClick={onClick}
                level={0}
              />
            ))}
          </ul>

          {isAdmin && (
            <Link
              to={getFullAdminRoute(ADMIN_ROUTES.faq)}
              className="faq-navbar__admin-link"
            >
              Перейти в админ панель
              <SettingOutlined />
            </Link>
          )}
        </nav>
      </aside>
    </>
  );
};

export default Navbar;
